import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import type { OperationLog } from "./operationTypes";

/** Readiness of the relayer serving this page's network. */
export function relayerStatusUrl(): string {
  const key = EVM_CONFIG.NETWORK?.registryKey;
  return key
    ? `/api/relayer-status?network=${encodeURIComponent(key)}`
    : "/api/relayer-status";
}

export interface RelayRoute {
  relay: boolean;
  relayerAddress?: string;
}

/** Decide before a proof is disclosed. A failed POST is never a fallback signal:
 * the relayer might have broadcast it even if its response was lost. */
export async function chooseFlatRelayRoute(params: {
  chainId: bigint;
  pool: string;
  fee?: bigint;
  signal?: AbortSignal;
  onLog: OperationLog;
  confirmFallback?: () => boolean | Promise<boolean>;
}): Promise<RelayRoute> {
  params.onLog("Checking relayer availability", "info");
  const controller = new AbortController();
  const abort = () => controller.abort();
  params.signal?.addEventListener("abort", abort, { once: true });
  if (params.signal?.aborted) controller.abort();
  const timer = setTimeout(abort, 15_000);
  let unavailable = false;
  try {
    let response: Response;
    try {
      response = await fetch(relayerStatusUrl(), {
        signal: controller.signal,
        cache: "no-store",
      });
    } catch {
      if (params.signal?.aborted)
        throw new DOMException("Operation cancelled", "AbortError");
      unavailable = true;
    }
    if (!unavailable) {
      let result: any;
      try {
        result = await response!.json();
      } catch {
        throw Error(
          "Relayer readiness response is invalid; wallet fallback is disabled",
        );
      }
      if (params.signal?.aborted)
        throw new DOMException("Operation cancelled", "AbortError");
      if (response!.status === 503 && result.code === "RELAYER_UNAVAILABLE") {
        unavailable = true;
      } else {
        if (
          !response!.ok ||
          result.ready !== true ||
          result.chainId !== Number(params.chainId) ||
          !ethers.isAddress(result.pool) ||
          ethers.getAddress(result.pool) !== ethers.getAddress(params.pool) ||
          !ethers.isAddress(result.relayerAddress) ||
          !/^[0-9]+$/.test(result.gasBalanceWei) ||
          BigInt(result.gasBalanceWei) <= 0n
        ) {
          throw Error(
            "Relayer readiness or configuration check failed; wallet fallback is disabled",
          );
        }
        return {
          relay: true,
          relayerAddress: ethers.getAddress(result.relayerAddress),
        };
      }
    }
  } finally {
    clearTimeout(timer);
    params.signal?.removeEventListener("abort", abort);
  }
  if (params.signal?.aborted)
    throw new DOMException("Operation cancelled", "AbortError");
  params.onLog(
    "Relayer unavailable; wallet submission requires your approval",
    "warning",
  );
  const confirm =
    params.confirmFallback ??
    (() =>
      globalThis.window?.confirm(
        "The relayer is unavailable. Submit this transaction from your connected wallet instead? This publicly links that wallet to the operation and you pay its gas. Any displayed withdrawal fee still applies. Cancel to wait for the relayer.",
      ) === true);
  if (!(await confirm()))
    throw Error(
      "Relayer unavailable. Operation stopped; no wallet transaction was requested.",
    );
  if (params.signal?.aborted)
    throw new DOMException("Operation cancelled", "AbortError");
  params.onLog(
    "Wallet fallback selected; the connected wallet will submit this operation",
    "warning",
  );
  return { relay: false };
}

export function assertFlatRelayFeeRecipient(
  route: RelayRoute,
  feeRecipient: string,
  fee: bigint,
): void {
  if (
    route.relay &&
    fee > 0n &&
    ethers.getAddress(feeRecipient) !== route.relayerAddress
  ) {
    throw Error(
      "Pool fee recipient does not match the relayer; proving stopped before submission",
    );
  }
}
